import React, { useState, useEffect, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";

// ── Colors ────────────────────────────────────────────────────
const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", goldDim: "#7A5A18", goldLight: "#F0C96A",
  text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#E24B4A", green: "#1D9E75", amber: "#BA7517",
  rule: "#2A3048",
};

const sev = { required: C.red, warning: C.gold, info: C.textDim };

// ── Helpers ───────────────────────────────────────────────────
const fmt = (d) => d ? new Date(d).toLocaleString() : "—";

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "20px 24px", flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.12em", color: C.textDim, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || C.text, lineHeight: 1.1 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, children, loading, error, borderColor, id }) {
  return (
    <div id={id} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderLeft: borderColor ? `3px solid ${borderColor}` : `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "24px 28px", marginBottom: 16 }}>
      {title && <div style={{ fontSize: 14, fontWeight: 600, color: C.gold, marginBottom: 14, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>{title}</div>}
      {loading ? <div style={{ color: C.textDim, fontStyle: "italic" }}>Loading...</div>
        : error ? <div style={{ color: C.amber, padding: 12, border: `1px solid ${C.amber}33`, borderRadius: 8, fontSize: 13 }}>Could not load — check API connection</div>
        : children}
    </div>
  );
}

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", textTransform: "uppercase", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text}</span>;
}

// ── Main Component ────────────────────────────────────────────
export default function ProcurementDashboard() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", "X-User": activeUser.identifier };

  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthErr, setHealthErr] = useState(null);

  const [flags, setFlags] = useState(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [flagsErr, setFlagsErr] = useState(null);

  const [oracle, setOracle] = useState(null);
  const [oracleLoading, setOracleLoading] = useState(true);
  const [oracleErr, setOracleErr] = useState(null);

  const [ingestRunning, setIngestRunning] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);

  const [toolResults, setToolResults] = useState({});
  const [toolRunning, setToolRunning] = useState({});

  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchHealth = useCallback(() => {
    setHealthLoading(true); setHealthErr(null);
    fetch(`${API}/procurement/health`, { headers: hdrs })
      .then(r => r.json()).then(d => { setHealth(d); setHealthLoading(false); })
      .catch(e => { setHealthErr(e.message); setHealthLoading(false); });
  }, [activeUser]);

  const fetchFlags = useCallback(() => {
    setFlagsLoading(true); setFlagsErr(null);
    fetch(`${API}/procurement/flags`, { headers: hdrs })
      .then(r => r.json()).then(d => { setFlags(d); setFlagsLoading(false); })
      .catch(e => { setFlagsErr(e.message); setFlagsLoading(false); });
  }, [activeUser]);

  const fetchOracle = useCallback(() => {
    setOracleLoading(true); setOracleErr(null);
    fetch(`${API}/procurement/admin/oracle/status`, { headers: hdrs })
      .then(r => r.json()).then(d => { setOracle(d); setOracleLoading(false); })
      .catch(e => { setOracleErr(e.message); setOracleLoading(false); });
  }, [activeUser]);

  const refreshAll = useCallback(() => {
    fetchHealth(); fetchFlags(); fetchOracle();
    setLastRefresh(new Date());
  }, [fetchHealth, fetchFlags, fetchOracle]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const dismissFlag = async (flagId, snooze) => {
    await fetch(`${API}/procurement/flags/${flagId}/dismiss`, {
      method: "POST", headers: hdrs,
      body: JSON.stringify(snooze ? { snooze_days: 7 } : {}),
    });
    fetchFlags();
  };

  const runIngest = async () => {
    setIngestRunning(true); setIngestResult(null);
    try {
      const r = await fetch(`${API}/procurement/admin/oracle/run`, { method: "POST", headers: hdrs });
      const d = await r.json();
      setIngestResult(d.stats || d);
      fetchOracle();
    } catch (e) { setIngestResult({ error: e.message }); }
    setIngestRunning(false);
  };

  const runTool = async (tool, params = {}) => {
    setToolRunning(p => ({ ...p, [tool]: true }));
    try {
      const r = await fetch(`${API}/procurement/chat`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({ tool, tool_params: params, message: "" }),
      });
      const d = await r.json();
      setToolResults(p => ({ ...p, [tool]: d.result || d }));
    } catch (e) { setToolResults(p => ({ ...p, [tool]: { error: e.message } })); }
    setToolRunning(p => ({ ...p, [tool]: false }));
  };

  const sc = health?.schema_counts || {};
  const reqCount = flags?.required_count || 0;
  const warnCount = flags?.warning_count || 0;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* ── HEADER ────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, borderBottom: `1px solid ${C.rule}`, paddingBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Procurement</h1>
            <div style={{ fontSize: 12, color: C.textSec, fontFamily: "monospace" }}>ADC — {health?.tenant || "loading..."}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {reqCount > 0 && (
              <a href="#flags-panel" style={{ textDecoration: "none" }}>
                <span style={{ background: C.red, color: "#fff", padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
                  {reqCount} REQUIRED
                </span>
              </a>
            )}
            {warnCount > 0 && (
              <a href="#flags-panel" style={{ textDecoration: "none" }}>
                <span style={{ background: `${C.gold}33`, color: C.gold, padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
                  {warnCount} WARNING
                </span>
              </a>
            )}
            <ProcurementUserSwitcher />
            <span style={{ fontSize: 11, color: C.textDim, fontFamily: "monospace" }}>{lastRefresh ? fmt(lastRefresh) : ""}</span>
            <button onClick={refreshAll} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" }}>Refresh</button>
          </div>
        </div>

        {/* ── STAT ROW ──────────────────────────────────── */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <StatCard label="Active Projects" value={sc.project_count ?? "—"} />
          <StatCard label="Planner Items" value={sc.vendor_count !== undefined ? (sc.user_count ?? "—") : "—"} sub={health?.user_type ? `as ${health.user_type}` : undefined} />
          <StatCard label="Open Flags" value={`${reqCount}/${warnCount}`} color={reqCount > 0 ? C.red : warnCount > 0 ? C.gold : C.green} sub={reqCount > 0 ? "required / warnings" : "required / warnings"} />
          <StatCard label="Unallocated PO Lines" value={oracle?.unallocated_po_lines ?? "—"} color={oracle?.unallocated_po_lines > 0 ? C.amber : C.green} />
        </div>

        {/* ── FLAGS ─────────────────────────────────────── */}
        <Panel id="flags-panel" title="Did You Remember" loading={flagsLoading} error={flagsErr} borderColor={reqCount > 0 ? C.red : undefined}>
          {flags?.flags?.length === 0 ? (
            <div style={{ color: C.green, fontSize: 13, fontFamily: "monospace" }}>No active flags</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(flags?.flags || []).slice(0, 10).map(f => (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: `${sev[f.severity] || C.textDim}11`, border: `1px solid ${sev[f.severity] || C.textDim}33`, borderRadius: 8 }}>
                  <Badge text={f.severity} color={sev[f.severity] || C.textDim} />
                  <div style={{ flex: 1, fontSize: 13, color: C.text }}>{f.message}</div>
                  <button onClick={() => dismissFlag(f.id, f.severity === "warning")} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontFamily: "monospace" }}>
                    {f.severity === "warning" ? "Snooze 7d" : "Dismiss"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* ── ORACLE INGEST STATUS ──────────────────────── */}
        <Panel title="Oracle Ingest Status" loading={oracleLoading} error={oracleErr}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 250 }}>
              <div style={{ fontSize: 12, color: C.textDim, fontFamily: "monospace", marginBottom: 8, textTransform: "uppercase" }}>PO Lines by Type</div>
              {(oracle?.po_lines_by_type || []).length === 0 ? <div style={{ color: C.textDim, fontSize: 12 }}>No PO lines ingested</div> : (
                (oracle.po_lines_by_type || []).map(r => (
                  <div key={r.line_type} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.rule}`, fontSize: 13 }}>
                    <span style={{ color: C.textSec, fontFamily: "monospace" }}>{r.line_type}</span>
                    <span style={{ color: C.text, fontWeight: 600, fontFamily: "monospace" }}>{r.count}</span>
                  </div>
                ))
              )}
            </div>
            <div style={{ flex: 1, minWidth: 250 }}>
              <div style={{ fontSize: 12, color: C.textDim, fontFamily: "monospace", marginBottom: 8, textTransform: "uppercase" }}>Planner Items by Status</div>
              {(oracle?.planner_items_by_status || []).length === 0 ? <div style={{ color: C.textDim, fontSize: 12 }}>No planner items</div> : (
                (oracle.planner_items_by_status || []).map(r => (
                  <div key={r.production_status} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.rule}`, fontSize: 13 }}>
                    <span style={{ color: C.textSec, fontFamily: "monospace" }}>{r.production_status}</span>
                    <span style={{ color: C.text, fontWeight: 600, fontFamily: "monospace" }}>{r.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={runIngest} disabled={ingestRunning} style={{ background: ingestRunning ? C.textDim : C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 600, fontSize: 12, cursor: ingestRunning ? "wait" : "pointer", fontFamily: "monospace" }}>
              {ingestRunning ? "Running..." : "Run Oracle Ingest"}
            </button>
            {ingestResult && !ingestResult.error && (
              <span style={{ fontSize: 12, color: C.green, fontFamily: "monospace" }}>
                {ingestResult.dry_run ? "DRY RUN: " : ""}
                {ingestResult.po_lines_fetched} lines fetched, {ingestResult.planner_rows_created} planner rows
              </span>
            )}
            {ingestResult?.error && <span style={{ fontSize: 12, color: C.red, fontFamily: "monospace" }}>{ingestResult.error}</span>}
          </div>
        </Panel>

        {/* ── AI TOOLS ──────────────────────────────────── */}
        <div style={{ fontSize: 14, fontWeight: 600, color: C.gold, marginBottom: 10, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}>AI Tools</div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>

          {/* Card A: Journal Entries */}
          <div style={{ flex: 1, minWidth: 280, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Find Journal Entries</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 12 }}>Identify PO lines needing cost moves</div>
            <button onClick={() => runTool("find_journal_entries")} disabled={toolRunning.find_journal_entries} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace", marginBottom: 10 }}>
              {toolRunning.find_journal_entries ? "Analyzing..." : "Run Analysis"}
            </button>
            {toolResults.find_journal_entries && (
              <div style={{ fontSize: 12, marginTop: 8 }}>
                <div style={{ color: C.textSec, fontFamily: "monospace" }}>{toolResults.find_journal_entries.message}</div>
                {(toolResults.find_journal_entries.entries || []).map((e, i) => (
                  <div key={i} style={{ marginTop: 8, padding: 10, background: `${C.amber}11`, borderRadius: 8, border: `1px solid ${C.amber}22` }}>
                    <div style={{ fontSize: 11, color: C.amber, fontFamily: "monospace" }}>{e.po_number}-{e.line_number} | ${Number(e.amount || 0).toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: C.textSec }}>{e.from_project} → {e.to_project}</div>
                    <div style={{ fontSize: 12, color: C.text, marginTop: 4 }}>{e.narrative}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Card B: PO Allocations */}
          <div style={{ flex: 1, minWidth: 280, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Suggest PO Allocations</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 12 }}>Flag unallocated lines + suggest projects</div>
            <button onClick={() => runTool("suggest_po_allocation")} disabled={toolRunning.suggest_po_allocation} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace", marginBottom: 10 }}>
              {toolRunning.suggest_po_allocation ? "Analyzing..." : "Run Analysis"}
            </button>
            {toolResults.suggest_po_allocation && (
              <div style={{ fontSize: 12, marginTop: 8 }}>
                <div style={{ color: C.textSec, fontFamily: "monospace" }}>{toolResults.suggest_po_allocation.message}</div>
                {(toolResults.suggest_po_allocation.suggestions || []).slice(0, 10).map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, padding: 8, background: `${C.bg}88`, borderRadius: 6 }}>
                    <span style={{ fontFamily: "monospace", color: C.textSec, fontSize: 11 }}>{s.po_number}-{s.line_number}</span>
                    <span style={{ flex: 1, fontSize: 11, color: C.text }}>{s.description?.slice(0, 40)}</span>
                    <Badge text={s.confidence || "—"} color={s.confidence === "high" ? C.green : s.confidence === "medium" ? C.amber : C.textDim} />
                    <span style={{ fontFamily: "monospace", fontSize: 11, color: C.gold }}>{s.suggested_allocation || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Card C: Cx Look Ahead */}
          <div style={{ flex: 1, minWidth: 280, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Cx Look Ahead</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 12 }}>Next 90 days across all active projects</div>
            <button onClick={() => runTool("generate_cx_lookahead", { lookahead_days: 90 })} disabled={toolRunning.generate_cx_lookahead} style={{ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace", marginBottom: 10 }}>
              {toolRunning.generate_cx_lookahead ? "Generating..." : "Generate"}
            </button>
            {toolResults.generate_cx_lookahead && (
              <div style={{ fontSize: 12, marginTop: 8 }}>
                <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                  <span style={{ color: C.gold, fontFamily: "monospace" }}>{toolResults.generate_cx_lookahead.item_count} items</span>
                  <span style={{ color: C.textSec, fontFamily: "monospace" }}>{toolResults.generate_cx_lookahead.project_count} projects</span>
                  <span style={{ color: C.textSec, fontFamily: "monospace" }}>{toolResults.generate_cx_lookahead.vendor_count} vendors</span>
                </div>
                {toolResults.generate_cx_lookahead.narrative && (
                  <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6, padding: 12, background: `${C.gold}08`, borderRadius: 8, borderLeft: `3px solid ${C.gold}` }}>
                    {toolResults.generate_cx_lookahead.narrative}
                  </div>
                )}
                <a href="/procurement/cx-lookahead" style={{ display: "inline-block", marginTop: 8, fontSize: 12, color: C.gold, textDecoration: "none" }}>View full Look Ahead &rarr;</a>
              </div>
            )}
          </div>
        </div>

        {/* ── RECENT ACTIVITY ───────────────────────────── */}
        <Panel title="Recent Activity">
          <div style={{ color: C.textDim, fontSize: 13, fontFamily: "monospace" }}>
            Activity feed — available once planner data is ingested.
          </div>
        </Panel>

      </div>
    </div>
  );
}
